'use client';
import React, { useState, useEffect, Component } from 'react';
import dynamic from 'next/dynamic';

const Sidebar = dynamic(() => import('./Sidebar'), {
  ssr: false,
  loading: () => (
    <div className="hidden lg:flex flex-col flex-shrink-0 w-60 bg-card border-r border-border" />
  ),
});

interface AppLayoutProps {
  children: React.ReactNode;
  activeRoute?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class SidebarErrorBoundary extends Component<
  { children: React.ReactNode },
  ErrorBoundaryState
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      // Render a placeholder sidebar on error so the layout doesn't break
      return (
        <div className="hidden lg:flex flex-col flex-shrink-0 w-60 bg-card border-r border-border" />
      );
    }
    return this.props.children;
  }
}

export default function AppLayout({ children, activeRoute }: AppLayoutProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <div className="flex min-h-screen bg-background">
      <SidebarErrorBoundary>
        {mounted && (
          <Sidebar activeRoute={activeRoute} collapsed={collapsed} setCollapsed={setCollapsed} />
        )}
      </SidebarErrorBoundary>
      <main className="flex-1 min-w-0 overflow-auto transition-all duration-300 ease-in-out">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-12 py-8">
          {children}
        </div>
      </main>
    </div>
  );
}